import React from 'react';
import { MenuItem } from '@headlessui/react';
import {
	useFindMySavedMarketStateFilters,
} from '@app/model/market_state/_saved_filters/service/SavedMarketStateFilterServiceHooks';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import _isEmpty from 'lodash/isEmpty';
import Utilities from '@util/Utilities';
import {
	SavedMarketStateFilterService,
} from '@app/model/market_state/_saved_filters/service/SavedMarketStateFilterService';
import SavedMarketStateFilter from '@app/model/market_state/_saved_filters/SavedMarketStateFilter';
import {
	useMarketStateFilterInputTools,
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { CheckIcon } from '@heroicons/react/20/solid';

const DashboardHeaderSavedFiltersSegment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	openSavedFiltersListDrawer: () => void
	openCreateSavedFilterModal: () => void
}> = ({ companyHotel, openSavedFiltersListDrawer, openCreateSavedFilterModal }) => {

	const [count, setCount] = React.useState<number>(0);

	React.useEffect(() => {
		SavedMarketStateFilterService.countMySavedMarketStateFilters(companyHotel.id).then(setCount);
	}, [companyHotel]);

	return <>
		<SavedFiltersList companyHotel={companyHotel} />
		<div className="py-1">
			<MenuItem as="div">
				{({ focus }) => (
					<a
						onClick={openCreateSavedFilterModal}
						className={Utilities.menuItemClasses(focus)}
					>
						<span className="font-semibold text-gray-900 block">Save Current Filters As</span>
					</a>
				)}
			</MenuItem>
			<MenuItem as="div">
				{({ focus }) => (
					<a
						onClick={openSavedFiltersListDrawer}
						className={Utilities.menuItemClasses(focus)}
					>
						<span className="font-semibold text-gray-900 block">See All Custom Filters<i
							className="text-gray-400 font-normal ml-1">({count})</i></span>
					</a>
				)}
			</MenuItem>
		</div>
	</>;
};

const SavedFiltersList: React.FunctionComponent<{ companyHotel: CompanyHotel }> = ({ companyHotel }) => {
	const { data, isSuccess } = useFindMySavedMarketStateFilters(companyHotel.id);

	if (isSuccess && !_isEmpty(data)) return <SavedFilters savedFilters={data!} />;

	return null;
};

const SavedFilters: React.FunctionComponent<{ savedFilters: SavedMarketStateFilter[]
}> = ({ savedFilters }) => {
	const { filter, setFilters } = useMarketStateFilterInputTools();
	const toaster = useToaster();

	const onApply = (savedFilter: SavedMarketStateFilter) => {
		const msFilter = { ...savedFilter.filter, competitorTagIds: filter.competitorTagIds };
		setFilters(msFilter);
		toaster.success('Saved Filters', 'Filter applied successfully');
	};

	const isSelected = (savedFilter: SavedMarketStateFilter) => {
		const saved = savedFilter.filter;
		const savedRoomTypes = [...saved.roomTypes].sort();
		const activeRoomTypes = [...filter.roomTypes].sort();

		const sameRoomTypes = savedRoomTypes.length === activeRoomTypes.length
			&& savedRoomTypes.every((roomType, index) => roomType === activeRoomTypes[index]);

		return sameRoomTypes
			&& saved.provider == filter.provider
			&& saved.platform == filter.platform
			&& saved.region == filter.region
			&& saved.ratesType == filter.ratesType
			&& saved.guests == filter.guests
			&& saved.nights == filter.nights
			&& saved.airportCode == filter.airportCode
			&& saved.meal == filter.meal
			&& saved.refunding == filter.refunding;
	};

	return <div className="py-1">
		{savedFilters.map((savedFilter) => <MenuItem as="div">
			{({ focus }) => (
				<a
					onClick={() => onApply(savedFilter)}
					className={Utilities.menuItemClasses(focus)}
				>
					<span className="font-semibold text-gray-900 block">{savedFilter.name}</span>
					{isSelected(savedFilter) &&
						<span className="text-indigo-600 absolute inset-y-0 right-0 flex items-center pr-4">
							<CheckIcon className="h-5 w-5 text-green-500" />
						</span>}
				</a>
			)}
		</MenuItem>)}
	</div>;
};


export default DashboardHeaderSavedFiltersSegment;